<!-- src/components/content/_modals/NotificationsModal.vue -->
<template>
  <div
    v-if="visible"
    class="modal-overlay"
  >
    <div class="modal-content">
      <h3>{{ t('components.notificationsHistory.modal.deleteByCriteria') }}</h3>
      <form @submit.prevent="confirmDeleteAll">
        <!-- Notification Types Selection -->
        <div class="form-group">
          <label>{{ t('components.notificationsHistory.modal.deleteByNotificationTypes') }}</label>
          <div
            class="multiselect-dropdown"
            @click.stop="toggleNotificationTypesDropdown"
          >
            <div class="selected-items">
              <span v-if="selectedNotificationTypes.length === 0">{{ t('components.notificationsHistory.modal.selectNotificationTypes') }}</span>
              <span v-else>{{ selectedNotificationTypesLabels.join(', ') }}</span>
            </div>
            <div class="dropdown-icon">
              <i :class="isNotificationTypesDropdownOpen ? 'icon-up' : 'icon-down'" />
            </div>
          </div>
          <div
            class="dropdown-options"
            v-if="isNotificationTypesDropdownOpen"
          >
            <div
              v-for="(option) in notificationTypeOptions"
              :key="option.value"
              class="dropdown-option"
            >
              <input
                type="checkbox"
                :id="'notification-type-' + option.value"
                :value="option.value"
                v-model="selectedNotificationTypes"
                @click.stop
              >
              <label :for="'notification-type-' + option.value">{{ option.label }}</label>
            </div>
          </div>
        </div>
        <!-- Vehicles Selection -->
        <div class="form-group">
          <label>{{ t('components.notificationsHistory.modal.deleteByVehiclesTypes') }}</label>
          <div
            class="multiselect-dropdown"
            @click.stop="toggleVehiclesDropdown"
          >
            <div class="selected-items">
              <span v-if="selectedVehicles.length === 0">{{ t('components.notificationsHistory.modal.selectVehicles') }}</span>
              <span v-else>{{ selectedVehiclesLabels.join(', ') }}</span>
            </div>
            <div class="dropdown-icon">
              <i :class="isVehiclesDropdownOpen ? 'icon-up' : 'icon-down'" />
            </div>
          </div>
          <div
            class="dropdown-options"
            v-if="isVehiclesDropdownOpen"
          >
            <div
              v-for="(option, index) in vehicleOptions"
              :key="option.value + '-' + index"
              class="dropdown-option"
            >
              <input
                type="checkbox"
                :id="'vehicle-' + option.value"
                :value="option.value"
                v-model="selectedVehicles"
                @click.stop
              >
              <label :for="'vehicle-' + option.value">{{ option.label }}</label>
            </div>
          </div>
        </div>
        <!-- Older Than Date Selection -->
        <div class="form-group">
          <label>{{ t('components.notificationsHistory.modal.deleteOlderThan') }}</label>
          <DatePicker
            v-model="deleteCriteria.olderThanDate"
            :range="false"
            @emitDate="updateOlderThanDate"
          />
        </div>
        <!-- Form Actions -->
        <div class="form-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeModal"
          >
            {{ t('components.vehicles.modals.exit') }}
          </button>
          <button
            type="submit"
            class="btn btn-danger"
          >
            {{ t('components.notificationsHistory.dataGrids.delete') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DatePicker from '@/components/extras/DatePicker.vue';
import { getCompanyFromJwt, getMyVehicles, getNotificationTypes } from '@/crud/getters';
import { massRemoveNotificationsHistoryByCriteria } from '@/crud/removers';
import eventBus from '@/core/plugins/eventBus';

export default defineComponent({
  name: 'NotificationsModal',
  components: {
    DatePicker,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'refreshDataGrid'],
  data() {
    return {
      companyId: parseInt(String(getCompanyFromJwt()), 10),
      deleteCriteria: {
        notificationTypes: [] as string[],
        vehicleImeis: [] as string[],
        olderThanDate: null as string | null,
        companyId: 0,
      },
      notificationTypeOptions: [] as { label: string; value: string }[],
      selectedNotificationTypes: [] as string[],
      isNotificationTypesDropdownOpen: false,
      vehicleOptions: [] as { label: string; value: string }[],
      selectedVehicles: [] as string[],
      isVehiclesDropdownOpen: false,
    };
  },
  computed: {
    t() {
      return this.$t;
    },
    selectedNotificationTypesLabels(): string[] {
      return this.notificationTypeOptions
          .filter((option) => this.selectedNotificationTypes.includes(option.value))
          .map((option) => option.label);
    },
    selectedVehiclesLabels(): string[] {
      return this.vehicleOptions
          .filter((option) => this.selectedVehicles.includes(option.value))
          .map((option) => option.label);
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
      this.resetDeleteCriteria();
    },
    loadNotificationTypeOptions() {
      getNotificationTypes()
          .then((response) => {
            const types = response.data.data || response.data;
            this.notificationTypeOptions = types.map((translateKey: string) => {
              const translationKey = `components.notificationsHistory.modal.${translateKey}`;
              const translated = this.t(translationKey);
              return {
                label: translated,
                value: translateKey,
              };
            });
          })
          .catch(() => {
            this.$toast.error(
                this.t('components.notificationsHistory.dataGrids.errorWhileLoadingNotificationTypes')
            );
          });
    },
    loadVehicleOptions() {
      getMyVehicles()
          .then((response) => {
            const vehicles = response.data;
            this.vehicleOptions = vehicles
                .map((v: any, index: number) => ({
                  label: v.registration || v.vars?.name || 'N/A',
                  value: v.device?.imei || `vehicle-${index}`,
                }))
                .filter((option) => option.value);
          })
          .catch(() => {
            this.$toast.error(
                this.t('components.notificationsHistory.dataGrids.errorWhileLoadingVehicles')
            );
          });
    },
    updateOlderThanDate(value: any) {
      this.deleteCriteria.olderThanDate = value;
    },
    resetDeleteCriteria() {
      this.deleteCriteria = {
        notificationTypes: [],
        vehicleImeis: [],
        olderThanDate: null,
        companyId: this.companyId,
      };
      this.selectedNotificationTypes = [];
      this.selectedVehicles = [];
    },
    confirmDeleteAll() {
      this.$q
          .dialog({
            title: this.t('components.notificationsHistory.modal.confirmationDeleteTitle'),
            message: this.t('components.notificationsHistory.modal.confirmationMessage'),
            cancel: true,
          })
          .onOk(() => {
            this.performMassDelete();
          });
    },
    performMassDelete() {
      this.deleteCriteria.notificationTypes = this.selectedNotificationTypes;
      this.deleteCriteria.vehicleImeis = this.selectedVehicles;
      this.deleteCriteria.companyId = this.companyId;

      massRemoveNotificationsHistoryByCriteria(this.deleteCriteria)
          .then((response) => {
            const deletedCount = response.data.deleted || 0;
            this.$emit('refreshDataGrid');
            this.$toast.success(
                this.t('components.notificationsHistory.dataGrids.recordsDeleted', {
                  count: deletedCount,
                })
            );
            eventBus.emit('notificationsRemoved');
            this.closeModal();
          })
          .catch(() => {
            this.$toast.error(
                this.t('components.notificationsHistory.dataGrids.errorWhileDeleting')
            );
          });
    },
    toggleNotificationTypesDropdown() {
      this.isNotificationTypesDropdownOpen = !this.isNotificationTypesDropdownOpen;
      if (this.isNotificationTypesDropdownOpen) {
        this.isVehiclesDropdownOpen = false;
      }
    },
    toggleVehiclesDropdown() {
      this.isVehiclesDropdownOpen = !this.isVehiclesDropdownOpen;
      if (this.isVehiclesDropdownOpen) {
        this.isNotificationTypesDropdownOpen = false;
      }
    },
    closeDropdowns(event: Event) {
      const target = event.target as HTMLElement;
      if (!target.closest('.multiselect-dropdown')) {
        this.isNotificationTypesDropdownOpen = false;
        this.isVehiclesDropdownOpen = false;
      }
    },
  },
  mounted() {
    this.deleteCriteria.companyId = this.companyId;
    this.loadNotificationTypeOptions();
    this.loadVehicleOptions();
    document.addEventListener('click', this.closeDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdowns);
  },
});
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  width: 500px;
  max-width: 90%;
  border-radius: 8px;
}

.form-group {
  margin-bottom: 15px;
  position: relative;
}

.multiselect-dropdown {
  position: relative;
  border: 1px solid #ccc;
  padding: 5px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-items {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  margin-left: 10px;
}

.dropdown-options {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid #ccc;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.dropdown-option {
  display: flex;
  align-items: center;
  padding: 5px;
}

.dropdown-option label {
  margin-left: 5px;
}

.icon-up::before {
  content: '▲';
}

.icon-down::before {
  content: '▼';
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn {
  margin-left: 10px;
}
</style>
